// Admin-Panel › Nutzer: Suche (entprellt), Filter, „Mehr laden" (nextCursor → after), Nutzer anlegen.
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { adminApi, type AdminUserFilter } from '../api';
import { displayName, formatBytes, formatDateTime } from '../format';
import { Button, Dialog, Empty, ErrorBox, Field, Loading, Tabs } from '../ui';
import { PASSWORD_MIN, USERNAME_RE, useDebounced } from './hooks';
import { AdminError, AdminSection, UserBadges } from './parts';
import { ADMIN_STATS_KEY, ADMIN_USERS_KEY } from './realtime';

type FilterValue = 'all' | AdminUserFilter;
const FILTERS: { value: FilterValue; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'admins', label: 'Admins' },
  { value: 'disabled', label: 'Gesperrt' },
  { value: 'bots', label: 'Bots' },
  { value: 'shadow', label: 'Schatten' },
];
const PAGE_SIZE = 50;

function CreateUserDialog(props: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [touched, setTouched] = useState(false);
  const usernameError = !USERNAME_RE.test(username) ? '2–32 Zeichen: Kleinbuchstaben, Ziffern, _ und .' : null;
  const passwordError = password.length < PASSWORD_MIN ? `Mindestens ${PASSWORD_MIN} Zeichen.` : null;
  const create = useMutation({
    mutationFn: () => adminApi.createUser({ username, password, email: email.trim() || undefined, isAdmin }),
    onSuccess: (u) => {
      void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
      void qc.invalidateQueries({ queryKey: ADMIN_STATS_KEY });
      props.onClose();
      void navigate(u.id);
    },
  });
  const submit = () => {
    setTouched(true);
    if (usernameError || passwordError) return;
    create.mutate();
  };
  return (
    <Dialog
      title="Nutzer anlegen"
      testId="admin-user-create-dialog"
      onClose={props.onClose}
      footer={
        <>
          <Button testId="admin-user-create-cancel" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" testId="admin-user-create-submit" disabled={create.isPending} onClick={submit}>
            Anlegen
          </Button>
        </>
      }
    >
      <form
        className="lfx-stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="Benutzername" error={touched ? usernameError : null}>
          {(id) => (
            <input
              id={id}
              className="lfx-input"
              value={username}
              autoComplete="off"
              data-testid="admin-user-create-username"
              onChange={(e) => setUsername(e.target.value)}
            />
          )}
        </Field>
        <Field label="Passwort" error={touched ? passwordError : null}>
          {(id) => (
            <input
              id={id}
              type="password"
              className="lfx-input"
              value={password}
              autoComplete="new-password"
              data-testid="admin-user-create-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <Field label="E-Mail (optional)">
          {(id) => (
            <input id={id} type="email" className="lfx-input" value={email} data-testid="admin-user-create-email" onChange={(e) => setEmail(e.target.value)} />
          )}
        </Field>
        <label className="lfx-check">
          <input type="checkbox" checked={isAdmin} data-testid="admin-user-create-admin" onChange={(e) => setIsAdmin(e.target.checked)} />
          Plattform-Admin
        </label>
        <ErrorBox error={create.error} testId="admin-user-create-error" />
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}

export function UsersPage() {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<FilterValue>('all');
  const [creating, setCreating] = useState(false);
  const closeCreate = useCallback(() => setCreating(false), []);
  const debounced = useDebounced(q.trim(), 300);
  const list = useInfiniteQuery({
    queryKey: [...ADMIN_USERS_KEY, 'list', debounced, filter],
    queryFn: ({ pageParam }) =>
      adminApi.users({ q: debounced || undefined, filter: filter === 'all' ? undefined : filter, after: pageParam, limit: PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const users = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <AdminSection
      title="Nutzer"
      testId="admin-users"
      actions={
        <Button variant="primary" testId="admin-users-create" onClick={() => setCreating(true)}>
          Nutzer anlegen
        </Button>
      }
    >
      <div className="lfx-stack">
        <Field label="Suche">
          {(id) => (
            <input
              id={id}
              type="search"
              className="lfx-input"
              value={q}
              placeholder="Benutzername, Anzeigename oder E-Mail"
              data-testid="admin-users-search"
              onChange={(e) => setQ(e.target.value)}
            />
          )}
        </Field>
        <Tabs value={filter} onChange={setFilter} items={FILTERS} testId="admin-users-filter" />
        {list.isLoading && <Loading testId="admin-users-loading" />}
        <AdminError error={list.error} testId="admin-users-error" />
        {list.isSuccess && users.length === 0 && <Empty testId="admin-users-empty">Keine Nutzer gefunden.</Empty>}
        {users.length > 0 && (
          <div className="lfx-table-wrap">
            <table className="lfx-table" data-testid="admin-users-list">
              <thead>
                <tr>
                  <th scope="col">Nutzer</th>
                  <th scope="col">E-Mail</th>
                  <th scope="col">Server</th>
                  <th scope="col">Speicher</th>
                  <th scope="col">Zuletzt aktiv</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    <td>
                      <Link to={u.id} className="lfx-link" data-testid={`admin-user-link-${u.id}`}>
                        {displayName(u)}
                      </Link>{' '}
                      <span className="lfx-muted">@{u.username}</span> <UserBadges user={u} />
                    </td>
                    <td>{u.email ?? '–'}</td>
                    <td>{u.serverCount}</td>
                    <td>{formatBytes(u.storageBytes)}</td>
                    <td>{formatDateTime(u.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list.hasNextPage && (
          <Button testId="admin-users-more" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {list.isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
          </Button>
        )}
      </div>
      {creating && <CreateUserDialog onClose={closeCreate} />}
    </AdminSection>
  );
}
